import { useQuery, useMutation } from '@tanstack/react-query'
import { apiService } from './apiService'
import { logger } from '../../config/logger'

/**
 * Fetch fee code data for catalog creation
 * @returns {Promise<Array>} - Array of fee codes with their details
 * @throws {Error} - If the API call fails
 */
export const FEE_CODES_CACHE_KEY = 'fee_codes_cache_v1'

const fetchFeeCodes = async () => {
    const readCache = () => {
        try {
            const raw =
                typeof window !== 'undefined' &&
                window.localStorage.getItem(FEE_CODES_CACHE_KEY)
            if (!raw) return null
            const parsed = JSON.parse(raw)
            return Array.isArray(parsed) ? parsed : null
        } catch (e) {
            console.warn('Failed to read fee codes cache:', e)
            return null
        }
    }

    const writeCache = (codes) => {
        try {
            if (typeof window === 'undefined') return
            window.localStorage.setItem(
                FEE_CODES_CACHE_KEY,
                JSON.stringify(codes)
            )
        } catch (e) {
            console.warn('Failed to write fee codes cache:', e)
        }
    }

    const endpoint = '/fee_codes'
    const url = `${apiService.getBaseUrl('v1')}${endpoint}`

    try {
        logger.info('fetch_fee_codes_start', {
            http: { method: 'GET', url },
        })
        const response = await apiService.get(endpoint, {}, 'v1')
        const payload = Array.isArray(response) ? response : response?.data

        if (Array.isArray(payload) && payload.length > 0) {
            const mapped = payload.map((fee) => ({
                id: fee.id,
                code: fee.code,
            }))
            writeCache(mapped)
            logger.info('fetch_fee_codes_success', {
                items_count: mapped.length,
            })
            return mapped
        }

        const cached = readCache()
        if (cached) return cached
        return []
    } catch (error) {
        const status = error?.response?.status
        logger.error('fetch_fee_codes_failure', {
            http: { method: 'GET', url, status_code: status },
            message_code: error?.message,
        })
        const cached = readCache()
        if (cached) return cached
        return []
    }
}

/**
 * Hook to fetch fee code data
 * @param {Object} options - Additional options for the query
 * @returns {Object} - Query result containing fee codes
 */
export const useFeeCodes = (options = {}) => {
    return useQuery({
        queryKey: ['feeCodes'],
        queryFn: fetchFeeCodes,
        ...options,
    })
}

/**
 * Fetch catalog data by search text
 * @param {string} searchText - Text to search for
 * @param {number} page - Page number (1-indexed)
 * @param {number} perPage - Number of items per page
 * @returns {Promise<Object>} - Catalog data
 */
const fetchCatalogsBySearch = async (searchText, page = 1, perPage = 10) => {
    const endpoint = `/evaluations?allNames=${encodeURIComponent(searchText)}&page=${page}&per_page=${perPage}`
    const url = `${apiService.getBaseUrl('v3')}${endpoint}`
    try {
        logger.info('fetch_catalogs_by_search_start', {
            http: { method: 'GET', url },
            search_text: searchText,
            page,
            per_page: perPage,
        })
        const response = await apiService.get(endpoint)
        const payload = Array.isArray(response) ? response : response?.data
        const itemsCount = Array.isArray(payload?.items)
            ? payload.items.length
            : Array.isArray(payload)
              ? payload.length
              : undefined
        logger.info('fetch_catalogs_by_search_success', {
            search_text: searchText,
            page,
            per_page: perPage,
            items_count: itemsCount,
        })
        return response
    } catch (error) {
        const status = error?.response?.status
        logger.error('fetch_catalogs_by_search_failure', {
            http: { method: 'GET', url, status_code: status },
            message_code: error?.message,
        })
        throw error
    }
}

/**
 * Fetch catalog data by ID
 * @param {string} evalId - Evaluation ID
 * @returns {Promise<Object>} - Catalog data
 */
const fetchCatalogById = async (evalId) => {
    if (!evalId) return null
    const endpoint = `/evaluations/${evalId}`
    const url = `${apiService.getBaseUrl('v1')}${endpoint}`
    try {
        logger.info('fetch_catalog_by_id_start', {
            http: { method: 'GET', url },
            eval_id: evalId,
        })
        const response = await apiService.get(endpoint, {}, 'v1')
        const itemsCount = Array.isArray(response?.items)
            ? response.items.length
            : Array.isArray(response?.data?.items)
              ? response.data.items.length
              : undefined
        logger.info('fetch_catalog_by_id_success', {
            eval_id: evalId,
            items_count: itemsCount,
        })
        return response
    } catch (error) {
        const status = error?.response?.status
        logger.error('fetch_catalog_by_id_failure', {
            http: { method: 'GET', url, status_code: status },
            eval_id: evalId,
            message_code: error?.message,
        })
        throw error
    }
}

/**
 * Fetch catalog data by URI
 * @param {string} uri - Evaluation URI
 * @returns {Promise<Object>} - Catalog data
 */
const fetchCatalogByUri = async (uri) => {
    if (!uri) return null
    const url = `${apiService.getBaseUrl('v3')}${uri}`
    try {
        logger.info('catalog_fetch_start', {
            http: { method: 'GET', url },
            uri,
        })
        const response = await apiService.get(uri)
        const itemsCount = Array.isArray(response?.items)
            ? response.items.length
            : Array.isArray(response?.data?.items)
              ? response.data.items.length
              : undefined
        logger.info('catalog_fetch_success', {
            uri,
            items_count: itemsCount,
        })
        return response
    } catch (error) {
        const status = error?.response?.status
        logger.error('catalog_fetch_failure', {
            http: { method: 'GET', url, status_code: status },
            uri,
            message_code: error?.message,
        })
        throw error
    }
}

/**
 * Fetch asset data by ID
 * @param {string} itemId - Item ID
 * @returns {Promise<Object>} - Asset data
 */
const fetchAssetById = async (itemId) => {
    if (!itemId) return null
    const endpoint = `/items/${itemId}`
    const url = `${apiService.getBaseUrl('v1')}${endpoint}`
    try {
        logger.info('fetch_asset_by_id_start', {
            http: { method: 'GET', url },
            item_id: itemId,
        })
        const response = await apiService.get(endpoint, {}, 'v1')
        logger.info('fetch_asset_by_id_success', {
            item_id: itemId,
        })
        return response
    } catch (error) {
        const status = error?.response?.status
        logger.error('fetch_asset_by_id_failure', {
            http: { method: 'GET', url, status_code: status },
            item_id: itemId,
            message_code: error?.message,
        })
        throw error
    }
}

/**
 * Fetch all users
 * @returns {Promise<Object>} - Users data
 */
const fetchAllUsers = async () => {
    const endpoint = '/all_users'
    const url = `${apiService.getBaseUrl('v3')}${endpoint}`
    try {
        logger.info('fetch_all_users_start', {
            http: { method: 'GET', url },
        })
        const response = await apiService.get(endpoint, {}, 'v3')
        const itemsCount = Array.isArray(response)
            ? response.length
            : Array.isArray(response?.data)
              ? response.data.length
              : undefined
        logger.info('fetch_all_users_success', {
            items_count: itemsCount,
        })
        return response
    } catch (error) {
        const status = error?.response?.status
        logger.error('fetch_all_users_failure', {
            http: { method: 'GET', url, status_code: status },
            message_code: error?.message,
        })
        throw error
    }
}

/**
 * Hook to fetch catalog data by search text
 * @param {string} searchText - Text to search for
 * @param {number} page - Page number (1-indexed)
 * @param {number} perPage - Number of items per page
 * @param {Object} options - Additional options for the query
 * @returns {Object} - Query result
 */
export const useCatalogSearch = (
    searchText,
    page = 1,
    perPage = 10,
    options = {}
) => {
    return useQuery({
        queryKey: ['catalogs', 'search', searchText, page, perPage],
        queryFn: () => fetchCatalogsBySearch(searchText, page, perPage),
        enabled: !!searchText,
        ...options,
    })
}

/**
 * Hook to fetch catalog data by ID
 * @param {string} evalId - Evaluation ID
 * @param {Object} options - Additional options for the query
 * @returns {Object} - Query result
 */
export const useCatalogById = (evalId, options = {}) => {
    return useQuery({
        queryKey: ['catalogs', 'id', evalId],
        queryFn: () => fetchCatalogById(evalId),
        enabled: !!evalId,
        ...options,
    })
}

/**
 * Hook to fetch catalog data by URI
 * @param {string} uri - Evaluation URI
 * @param {Object} options - Additional options for the query
 * @returns {Object} - Query result
 */
export const useCatalogByUri = (uri, options = {}) => {
    return useQuery({
        queryKey: ['catalogs', 'uri', uri],
        queryFn: () => fetchCatalogByUri(uri),
        enabled: !!uri,
        ...options,
    })
}

/**
 * Hook to fetch asset data by ID
 * @param {string} itemId - Item ID
 * @param {Object} options - Additional options for the query
 * @returns {Object} - Query result
 */
export const useAssetById = (itemId, options = {}) => {
    return useQuery({
        queryKey: ['assets', 'id', itemId],
        queryFn: () => fetchAssetById(itemId),
        enabled: !!itemId,
        ...options,
    })
}

/**
 * Hook to fetch all users
 * @param {Object} options - Additional options for the query
 * @returns {Object} - Query result
 */
export const useAllUsers = (options = {}) => {
    return useQuery({
        queryKey: ['users', 'all'],
        queryFn: () => fetchAllUsers(),
        staleTime: 5 * 60 * 1000, // 5 minutes
        ...options,
    })
}

/**
 * Fetch employees data
 * @returns {Promise<Object>} - Employees data
 */
const fetchEmployees = async () => {
    const endpoint = '/employees'
    const url = `${apiService.getBaseUrl('v3')}${endpoint}`
    try {
        logger.info('fetch_employees_start', {
            http: { method: 'GET', url },
        })
        const response = await apiService.get(endpoint)
        const itemsCount = Array.isArray(response)
            ? response.length
            : Array.isArray(response?.data)
              ? response.data.length
              : undefined
        logger.info('fetch_employees_success', {
            items_count: itemsCount,
        })
        return response
    } catch (error) {
        const status = error?.response?.status
        logger.error('fetch_employees_failure', {
            http: { method: 'GET', url, status_code: status },
            message_code: error?.message,
        })
        throw error
    }
}

/**
 * Hook to fetch employees data
 * @param {Object} options - Additional options for the query
 * @returns {Object} - Query result
 */
export const useEmployees = (options = {}) => {
    return useQuery({
        queryKey: ['employees'],
        queryFn: fetchEmployees,
        staleTime: 5 * 60 * 1000, // 5 minutes
        ...options,
    })
}

/**
 * Fetch packages data
 * @param {string} employeesParam - Comma separated employee IDs
 * @param {string} statusParam - Comma separated statuses
 * @param {number} pageNumber - Page number (0-indexed)
 * @param {number} rowsPerPage - Number of rows per page
 * @returns {Promise<Object>} - Packages data
 */
const fetchPackages = async (
    employeesParam,
    statusParam,
    pageNumber,
    rowsPerPage
) => {
    const endpoint = `/packages?page=${pageNumber + 1}&size=${rowsPerPage}&by_user=${employeesParam}&status=${statusParam}`
    const url = `${apiService.getBaseUrl('v3')}${endpoint}`
    try {
        logger.info('fetch_packages_start', {
            http: { method: 'GET', url },
            employees_param: employeesParam,
            status_param: statusParam,
            page_number: pageNumber,
            rows_per_page: rowsPerPage,
        })
        const response = await apiService.get(endpoint)
        const itemsCount = Array.isArray(response?.items)
            ? response.items.length
            : Array.isArray(response)
              ? response.length
              : undefined
        logger.info('fetch_packages_success', {
            items_count: itemsCount,
            employees_param: employeesParam,
            status_param: statusParam,
            page_number: pageNumber,
            rows_per_page: rowsPerPage,
        })
        return response
    } catch (error) {
        const status = error?.response?.status
        logger.error('fetch_packages_failure', {
            http: { method: 'GET', url, status_code: status },
            employees_param: employeesParam,
            status_param: statusParam,
            page_number: pageNumber,
            rows_per_page: rowsPerPage,
            message_code: error?.message,
        })
        throw error
    }
}

/**
 * Hook to fetch packages data
 * @param {Object} params - Parameters for the query
 * @param {string[]} params.selectedEmployees - Selected employees
 * @param {string[]} params.filter - Status filters
 * @param {number} params.pageNumber - Page number (0-indexed)
 * @param {number} params.rowsPerPage - Number of rows per page
 * @param {Object} options - Additional options for the query
 * @returns {Object} - Query result
 */
export const usePackages = (
    { selectedEmployees, filter, pageNumber, rowsPerPage },
    options = {}
) => {
    const employeesParam = selectedEmployees?.join(',') || ''
    const statusParam = filter?.join(',') || ''

    return useQuery({
        queryKey: [
            'packages',
            employeesParam,
            statusParam,
            pageNumber,
            rowsPerPage,
        ],
        queryFn: () =>
            fetchPackages(employeesParam, statusParam, pageNumber, rowsPerPage),
        ...options,
    })
}
